import React, { type ComponentType } from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import { token } from '@atlaskit/tokens';
import ViewTracker from '@atlassian/jira-analytics-web-react/src/components/view-tracker.tsx';
import type { AdditionalEventAttributes } from '@atlassian/jira-common-experience-tracking-analytics';
import OpTrigger from '@atlassian/jira-common-util-op-triggers/src/on-mount';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import SubmitApdex from '@atlassian/jira-providers-spa-apdex-analytics/src/submit-apdex-mark/index.tsx';
import type { EstimationStatistic } from '@atlassian/jira-software-report-charting-common/src/common/utils/format-estimate/index.tsx';
import Spinner from '@atlassian/jira-software-report-charting-common/src/ui/spinner/index.tsx';
import { SpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import { isErrorTrackingEnabledWithSentry, isPollinatorTenant } from '../feature-flags';
import { velocityReportPageLoad } from '../services/utils/performance-analytics';
import ErrorEmptyState from './error-empty-state';
import VelocityErrorToSLA from './velocity-error-to-sla';

export type Props = {
	isFirstDataLoaded: boolean;
	isError: boolean;
	fetchError: Error | null;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	Chart: ComponentType<Record<any, any>>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	Grid: ComponentType<Record<any, any>>;
	estimationStatistic: EstimationStatistic;
	onExperienceSuccess: () => void;
	onExperienceFailure: (extraLocation?: string, attributes?: AdditionalEventAttributes) => void;
};

export default function VelocityReport(props: Props) {
	const {
		Chart,
		Grid,
		isFirstDataLoaded,
		isError,
		onExperienceSuccess,
		onExperienceFailure,
		fetchError,
		estimationStatistic,
	} = props;

	const loadedComponent = isFirstDataLoaded ? (
		<div>
			<Chart />
			<Grid />

			<OpTrigger op={onExperienceSuccess} />
		</div>
	) : (
		<Spinner />
	);

	return (
		<Container data-test-id="software-velocity-report.root-container">
			{__SPA__ && isFirstDataLoaded && (
				<>
					<SubmitApdex
						appName="software-reports-velocity-report"
						ttrSlo={8000}
						ttiSlo={8000}
						extra={{
							isSentryEnabled: String(isErrorTrackingEnabledWithSentry()),
							synthetic: isPollinatorTenant(),
						}}
						// @ts-expect-error - TS2769 - No overload matches this call.
						metric={velocityReportPageLoad}
					/>
					<SpaStatePageReady />
				</>
			)}

			{isError ? (
				<>
					<ErrorEmptyState />
					<VelocityErrorToSLA error={fetchError} onExperienceFailure={onExperienceFailure} />
				</>
			) : (
				<ContextualAnalyticsData
					attributes={{
						estimationStatistic,
					}}
				>
					{loadedComponent}
				</ContextualAnalyticsData>
			)}

			<ViewTracker />
		</Container>
	);
}

VelocityReport.defaultProps = {
	fetchError: null,
	onExperienceSuccess: noop,
	onExperienceFailure: noop,
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	padding: `${token('space.100', '8px')} 0`,
});
